<template>
  <div class="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col">
    <header class="bg-emerald-600 text-white py-6 px-4">
      <div class="max-w-4xl mx-auto">
        <a :href="backUrl" class="text-emerald-200 hover:text-white text-sm">&larr; Back to Home</a>
        <h1 class="text-2xl font-bold mt-1">My Deck</h1>
        <p class="text-emerald-100 text-sm mt-1">
          Words you saved while reading &mdash; tap any word in a story or grammar example to add it.
        </p>
      </div>
    </header>

    <main class="flex-1 max-w-4xl mx-auto w-full px-4 py-6">
      <div v-if="loading" class="text-center py-16">
        <div class="inline-block animate-spin rounded-full h-8 w-8 border-4 border-emerald-200 border-t-emerald-600"></div>
      </div>

      <!-- Empty -->
      <div v-else-if="!words.length" class="bg-white rounded-xl border border-gray-200 p-8 text-center dark:bg-gray-800 dark:border-gray-700">
        <p class="text-4xl">&#128218;</p>
        <h2 class="mt-3 text-lg font-semibold text-gray-900 dark:text-gray-50">Your deck is empty</h2>
        <p class="mt-2 text-sm text-gray-500 max-w-sm mx-auto dark:text-gray-400">
          While you're reading a story or a grammar example, tap any underlined word to hear it,
          see what it means, and add it here.
        </p>
        <a :href="backUrl" class="inline-block mt-5 bg-emerald-600 text-white px-6 py-2.5 rounded-lg font-medium hover:bg-emerald-700">
          Find a story to read
        </a>
      </div>

      <template v-else>
        <!-- Study actions -->
        <div class="bg-white rounded-xl border border-gray-200 p-5 dark:bg-gray-800 dark:border-gray-700">
          <div class="flex items-baseline justify-between gap-3 flex-wrap">
            <p class="font-semibold text-gray-900 dark:text-gray-50">
              {{ words.length }} saved {{ words.length === 1 ? 'word' : 'words' }}
              <span v-if="dueCount" class="text-emerald-600 font-medium">&middot; {{ dueCount }} due</span>
            </p>
          </div>

          <div class="mt-4 flex flex-wrap gap-2">
            <a :href="studyUrl()" class="bg-emerald-600 text-white px-5 py-2 rounded-lg font-medium hover:bg-emerald-700">
              Study all
            </a>
            <a
              v-if="words.length > SUBSET_SIZE"
              :href="studyUrl({ shuffle: 1, limit: SUBSET_SIZE })"
              class="bg-white text-gray-700 border border-gray-300 px-5 py-2 rounded-lg font-medium hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-200 dark:border-gray-600 dark:hover:bg-gray-700"
            >
              Random {{ SUBSET_SIZE }}
            </a>
          </div>
        </div>

        <!-- Grouped by the text each word was saved from -->
        <section v-for="group in groups" :key="group.key" class="mt-6">
          <div class="flex items-baseline justify-between gap-3">
            <h2 class="font-semibold text-gray-900 dark:text-gray-50">{{ group.label }}</h2>
            <a
              v-if="group.source"
              :href="studyUrl({ source_kind: group.source.kind, source_id: group.source.id })"
              class="text-sm font-medium text-emerald-700 hover:underline shrink-0"
            >Study these {{ group.words.length }} &rarr;</a>
          </div>

          <ul class="mt-2 bg-white rounded-xl border border-gray-200 divide-y divide-gray-100 dark:bg-gray-800 dark:border-gray-700 dark:divide-gray-700">
            <li v-for="word in group.words" :key="word.vocabulary_word_id" class="flex items-center gap-3 px-4 py-3">
              <AudioButton :src="word.audio_url" :text="word.word_vietnamese" :show-slow="true" class="shrink-0" />
              <div class="min-w-0 flex-1">
                <p class="font-semibold text-gray-900 truncate dark:text-gray-50">{{ word.word_vietnamese }}</p>
                <p class="text-sm text-gray-500 truncate dark:text-gray-400">{{ word.word_english }}</p>
              </div>
              <StrengthDots :strength="strengthOf(word)" class="shrink-0" />
              <button
                type="button"
                :aria-label="`Remove ${word.word_vietnamese} from your deck`"
                class="shrink-0 text-gray-300 hover:text-red-500 transition-colors px-1"
                @click="remove(word)"
              >&times;</button>
            </li>
          </ul>
        </section>
      </template>
    </main>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { apiGet, apiDelete } from '../api/client'
import AudioButton from './shared/AudioButton.vue'
import StrengthDots from './shared/StrengthDots.vue'

defineProps({
  backUrl: { type: String, required: true },
})

// Big enough to be a real session, small enough to finish in a sitting.
const SUBSET_SIZE = 20

const words = ref([])
const loading = ref(true)

const dueCount = computed(() => words.value.filter(w => w.due).length)

// Mirrors VocabReview#strength on the server.
function strengthOf(word) {
  if (word.review_id == null) return 0
  if (!word.repetitions) return 1
  if (word.interval_days >= 21) return 4
  if (word.interval_days >= 6) return 3
  return 2
}

const SOURCE_LABELS = {
  story: 'From a story',
  conversation: 'From a conversation',
  grammar: 'From grammar examples',
  phrasebook: 'From the phrasebook',
  lesson: 'From a lesson',
  word_page: 'Saved from a word page',
}

// One group per text the words came from, so "study just this story" is a real
// button rather than a filter the learner has to construct.
const groups = computed(() => {
  const buckets = new Map()

  for (const word of words.value) {
    const key = word.source_kind && word.source_id
      ? `${word.source_kind}:${word.source_id}`
      : 'other'

    if (!buckets.has(key)) {
      buckets.set(key, {
        key,
        label: key === 'other' ? 'Saved words' : (SOURCE_LABELS[word.source_kind] ?? 'Saved words'),
        source: key === 'other' ? null : { kind: word.source_kind, id: word.source_id },
        words: [],
      })
    }
    buckets.get(key).words.push(word)
  }

  return [...buckets.values()]
})

function studyUrl(params = {}) {
  const query = new URLSearchParams({ mode: 'deck', ...params })
  return `/review/vocab?${query.toString()}`
}

async function remove(word) {
  const previous = words.value
  words.value = words.value.filter(w => w.vocabulary_word_id !== word.vocabulary_word_id)

  try {
    await apiDelete(`/api/v1/deck/${word.vocabulary_word_id}`)
  } catch (e) {
    words.value = previous
  }
}

onMounted(async () => {
  try {
    const data = await apiGet('/api/v1/deck')
    words.value = data?.words ?? []
  } catch (e) {
    words.value = []
  } finally {
    loading.value = false
  }
})
</script>
